import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Users, Plus, X, Loader2, Edit2, Trash2, Calculator, Calendar } from "lucide-react";
import { HelpTip } from "@/components/HelpTip";

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", type: "internal", role: "", department: "", daily_cost: 0, cost_per_hour: null, available_hours_per_week: 40, capacity_override_pct: 100, capacity_override_note: "" });
  const [assignForm, setAssignForm] = useState({ project_id: "", resource_id: "", hours_allocated: 0, start_date: "", end_date: "", role_in_project: "" });
  const [saving, setSaving] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [calcHours, setCalcHours] = useState(0);
  const [calcResource, setCalcResource] = useState("");
  const [calcResult, setCalcResult] = useState(null);
  // Iter313 · O02 — resumen de capacidad (vacaciones/BAU/dedicación bloqueada)
  const [capacity, setCapacity] = useState(null);
  const [overrideEdit, setOverrideEdit] = useState(null); // {resource_id, pct, note}
  const loadCapacity = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/resources/capacity-summary`); setCapacity(data); }
    catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async () => {
    try {
      const [rRes, aRes, pRes] = await Promise.all([
        axios.get(`${API}/resources`),
        axios.get(`${API}/resource-assignments`),
        axios.get(`${API}/projects`),
      ]);
      setResources(rRes.data);
      setAssignments(aRes.data);
      setProjects(pRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); loadCapacity(); }, [load, loadCapacity]);

  // Close modal helper
  const closeModal = () => { setShowModal(false); setEditingResource(null); };

  // Escape key handler
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && showModal) closeModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]); // eslint-disable-line

  const EMPTY_FORM = { name: "", email: "", type: "internal", role: "", department: "", daily_cost: 0, cost_per_hour: null, available_hours_per_week: 40, capacity_override_pct: 100, capacity_override_note: "" };

  const openEdit = (r) => {
    setEditingResource(r);
    setForm({ name: r.name, email: r.email, type: r.type, role: r.role, department: r.department, daily_cost: r.daily_cost, cost_per_hour: r.cost_per_hour || null, available_hours_per_week: r.available_hours_per_week, capacity_override_pct: r.capacity_override_pct ?? 100, capacity_override_note: r.capacity_override_note || "" });
    setShowModal(true);
  };

  const openCreate = () => {
    setEditingResource(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource.id}`, form);
      } else {
        await axios.post(`${API}/resources`, form);
      }
      setShowModal(false);
      setEditingResource(null);
      setForm(EMPTY_FORM);
      load(); loadCapacity();
    } catch (e) { alert("Error al guardar el recurso"); }
    finally { setSaving(false); }
  };

  const saveOverride = async () => {
    if (!overrideEdit) return;
    try {
      await axios.put(`${API}/resources/${overrideEdit.resource_id}/capacity-override`, {
        capacity_override_pct: overrideEdit.pct, capacity_override_note: overrideEdit.note,
      });
      setOverrideEdit(null);
      load(); loadCapacity();
    } catch (e) { alert(e.response?.data?.detail || "Error al ajustar la dedicación"); }
  };

  const handleAssign = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await axios.post(`${API}/resource-assignments`, assignForm);
      setShowAssignModal(false);
      setCalcResult(data);
      load();
    } catch (e) { alert("Error"); }
    finally { setSaving(false); }
  };

  const [confirmDelete, setConfirmDelete] = useState(null); // resource id to confirm

  const handleDelete = async (id) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/resources/${id}`);
      setResources(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      alert(e.response?.data?.detail || "Error al eliminar el recurso");
    }
  };

  const calculateImpact = () => {
    const res = resources.find(r => r.id === calcResource);
    if (!res || !calcHours) return;
    const weeks = calcHours / res.available_hours_per_week;
    const days = Math.ceil(weeks * 5);
    const cost = (calcHours / 8) * res.daily_cost;
    setCalcResult({ days, cost, weeks: weeks.toFixed(1) });
  };

  const internal = resources.filter(r => r.type === "internal");
  const external = resources.filter(r => r.type === "external");

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#007934]" /></div>;

  return (
    <div className="space-y-5 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Gestión de Recursos</h1>
          <p className="text-sm text-gray-500 mt-0.5">{resources.length} recursos · {internal.length} internos · {external.length} externos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAssignModal(true)} className="border border-[#007934] text-[#007934] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#E8F5E9]" data-testid="assign-resource-btn">
            Asignar a proyecto
          </button>
          <button onClick={openCreate} className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] flex items-center gap-2" data-testid="add-resource-btn">
            <Plus size={16} /> Nuevo recurso
          </button>
        </div>
      </div>

      {/* Impact Calculator */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2"><Calculator size={16} className="text-[#007934]" />Calculadora de Impacto</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Recurso</label>
            <select value={calcResource} onChange={e => setCalcResource(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="calc-resource-select">
              <option value="">Seleccionar...</option>
              {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Horas necesarias</label>
            <input type="number" value={calcHours} onChange={e => setCalcHours(parseInt(e.target.value)||0)} min="0"
              className="w-32 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="calc-hours-input" />
          </div>
          <button onClick={calculateImpact} className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]" data-testid="calculate-btn">
            Calcular
          </button>
          {calcResult && !calcResult.items_synced && (
            <div className="bg-[#E8F5E9] rounded-lg px-4 py-2 text-sm">
              <span className="text-[#007934] font-semibold">{calcResult.days} días</span>
              <span className="text-gray-500 mx-2">·</span>
              <span className="text-gray-700">{calcResult.weeks} semanas</span>
              <span className="text-gray-500 mx-2">·</span>
              <span className="text-gray-700">Coste: {calcResult.cost?.toLocaleString("es-ES")}€</span>
            </div>
          )}
        </div>
      </div>

      {/* Iter313 · O02 — Capacidad por persona: suma la carga de TODAS las
          asignaciones activas de cada recurso frente a su capacidad
          efectiva (horas/semana × % de dedicación activa), para detectar
          sobre-asignación (caso real: una persona al 110%). El % de
          dedicación se ajusta manualmente para reflejar vacaciones/BAU
          mientras no haya un calendario de ausencias propio. */}
      {capacity && capacity.resources.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="capacity-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <Calendar size={16} className="text-[#007934]" /> Capacidad por persona
              <HelpTip text="% = horas/semana cargadas en TODAS sus asignaciones activas ÷ capacidad efectiva (horas/semana × % de dedicación activa). Por encima de 100% significa sobre-asignación." />
            </h3>
            {capacity.overallocated_count > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-semibold" data-testid="overallocated-count">
                {capacity.overallocated_count} sobre-asignada{capacity.overallocated_count === 1 ? "" : "s"}
              </span>
            )}
            {capacity.without_assignment_data_count > 0 && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full" data-testid="no-assignment-data-count">
                {capacity.without_assignment_data_count} sin datos de asignación
              </span>
            )}
          </div>
          <div className="space-y-2">
            {capacity.resources.map(r => {
              const barColor = r.overallocated ? "bg-red-500" : r.allocation_pct >= 85 ? "bg-amber-500" : "bg-[#007934]";
              const isEditing = overrideEdit?.resource_id === r.resource_id;
              return (
                <div key={r.resource_id} className={`rounded-lg border p-3 ${r.overallocated ? "border-red-200 bg-red-50/40" : "border-gray-50"}`} data-testid={`capacity-row-${r.resource_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {r.name}
                        {r.merged_duplicate_count > 0 && (
                          <span className="ml-1.5 text-[10px] font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded" data-testid={`capacity-merged-${r.resource_id}`}>
                            fusionado de {r.merged_duplicate_count + 1} registros de alias
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        {r.weekly_load}h/sem cargadas · capacidad efectiva {r.effective_capacity}h/sem
                        {r.capacity_override_pct < 100 && <span className="text-amber-600"> ({r.capacity_override_pct}% dedicación — {r.capacity_override_note || "ajuste manual"})</span>}
                      </p>
                    </div>
                    <span className={`text-sm font-bold ${r.overallocated ? "text-red-600" : !r.has_assignment_data ? "text-gray-400" : "text-gray-700"}`} data-testid={`capacity-pct-${r.resource_id}`}>
                      {r.has_assignment_data ? `${r.allocation_pct}%` : "— sin datos"}
                    </span>
                    <button onClick={() => setOverrideEdit(isEditing ? null : { resource_id: r.resource_id, pct: r.capacity_override_pct, note: r.capacity_override_note })}
                      className="text-[11px] text-[#007934] hover:underline flex-shrink-0" data-testid={`capacity-adjust-btn-${r.resource_id}`}>
                      {isEditing ? "Cerrar" : "Ajustar"}
                    </button>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2 overflow-hidden">
                    <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${Math.min(r.allocation_pct, 100)}%` }} />
                  </div>
                  {r.assignments.some(a => a.is_estimate) && (
                    <p className="text-[10px] text-gray-400 mt-1 italic">Algunas asignaciones no tienen fecha de inicio/fin — su h/semana es estimada, no confirmada.</p>
                  )}
                  {!r.has_assignment_data && (
                    <p className="text-[10px] text-gray-400 mt-1 italic" data-testid={`capacity-no-data-note-${r.resource_id}`}>
                      Sin asignaciones registradas todavía — este 0% no significa &quot;confirmado libre&quot;, significa que aún no hay datos de carga para esta persona.
                    </p>
                  )}
                  {isEditing && (
                    <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-end gap-2" data-testid={`capacity-edit-${r.resource_id}`}>
                      {r.merged_duplicate_count > 0 && (
                        <p className="text-[10px] text-amber-600 w-full">El ajuste se aplica solo al primer registro fusionado — revisa los {r.merged_duplicate_count + 1} alias si hace falta ajustar otro.</p>
                      )}
                      <div>
                        <label className="text-[10px] text-gray-500 block mb-0.5">% dedicación activa</label>
                        <input type="number" min="0" max="100" value={overrideEdit.pct}
                          onChange={e => setOverrideEdit({ ...overrideEdit, pct: parseInt(e.target.value) || 0 })}
                          className="w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" data-testid="capacity-pct-input" />
                      </div>
                      <div className="flex-1 min-w-[160px]">
                        <label className="text-[10px] text-gray-500 block mb-0.5">Motivo (vacaciones, BAU...)</label>
                        <input value={overrideEdit.note} onChange={e => setOverrideEdit({ ...overrideEdit, note: e.target.value })}
                          placeholder="Ej: vacaciones hasta 20/11"
                          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" data-testid="capacity-note-input" />
                      </div>
                      <button onClick={saveOverride} className="bg-[#007934] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#006528]" data-testid="capacity-save-btn">
                        Guardar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resources Grid */}
      <div className="grid grid-cols-1 gap-4">
        {/* Internal */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Recursos Internos ({internal.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="internal-resources-table">
              <thead><tr className="text-xs text-gray-400 uppercase border-b border-gray-50">
                <th className="text-left pb-2 pr-4">Nombre</th>
                <th className="text-left pb-2 pr-4">Rol</th>
                <th className="text-left pb-2 pr-4">Departamento</th>
                <th className="text-left pb-2 pr-4">Coste/día</th>
                <th className="text-left pb-2 pr-4">H/semana</th>
                <th></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {internal.map(r => (
                  <tr key={r.id} data-testid={`resource-row-${r.id}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#007934] text-xs font-bold">
                          {r.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{r.name}</p>
                          <p className="text-xs text-gray-400">{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600">{r.role}</td>
                    <td className="py-3 pr-4 text-sm text-gray-600">{r.department}</td>
                    <td className="py-3 pr-4 text-sm font-medium text-gray-900">
                      {r.daily_cost > 0 ? (
                        <span>{r.daily_cost}€/día
                          {r.cost_per_hour ? <span className="ml-1 text-xs text-gray-400">({r.cost_per_hour}€/h)</span> : null}
                        </span>
                      ) : <span className="text-gray-400 text-xs">Sin coste</span>}
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600">{r.available_hours_per_week}h</td>
                    <td className="py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(r)} className="text-gray-300 hover:text-[#007934] transition-colors p-1" title="Editar" data-testid={`edit-resource-${r.id}`}><Edit2 size={14} /></button>
                        {confirmDelete === r.id ? (
                          <span className="flex items-center gap-1 text-xs">
                            <button onClick={() => handleDelete(r.id)} className="text-white bg-red-500 hover:bg-red-600 px-2 py-0.5 rounded text-xs font-medium" data-testid={`confirm-delete-${r.id}`}>Borrar</button>
                            <button onClick={() => setConfirmDelete(null)} className="text-gray-500 hover:text-gray-700 px-1 py-0.5 rounded text-xs">✕</button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmDelete(r.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1" title="Eliminar" data-testid={`delete-resource-${r.id}`}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* External */}
        {external.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Recursos Externos ({external.length})</h3>
            <div className="space-y-2">
              {external.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-50 hover:bg-gray-50">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-bold">
                    {r.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{r.name}</p>
                    <p className="text-xs text-gray-400">{r.role} · {r.email}</p>
                  </div>
                  <span className="text-sm text-gray-600 font-medium">
                    {r.daily_cost > 0 ? `${r.daily_cost}€/día` : "Sin coste"}
                    {r.cost_per_hour ? <span className="text-xs text-gray-400 ml-1">· {r.cost_per_hour}€/h</span> : null}
                  </span>
                  <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">Externo</span>
                  <button onClick={() => openEdit(r)} className="text-gray-300 hover:text-[#007934] transition-colors p-1" title="Editar"><Edit2 size={14} /></button>
                  {confirmDelete === r.id ? (
                    <span className="flex items-center gap-1 text-xs">
                      <button onClick={() => handleDelete(r.id)} className="text-white bg-red-500 hover:bg-red-600 px-2 py-0.5 rounded text-xs font-medium" data-testid={`confirm-delete-ext-${r.id}`}>Borrar</button>
                      <button onClick={() => setConfirmDelete(null)} className="text-gray-500 px-1 py-0.5 rounded text-xs">✕</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmDelete(r.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1" title="Eliminar" data-testid={`delete-resource-ext-${r.id}`}><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assignments */}
        {assignments.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-900 font-['Outfit'] mb-4">Asignaciones a Proyectos ({assignments.length})</h3>
            <div className="space-y-2">
              {assignments.map(a => {
                const res = resources.find(r => r.id === a.resource_id);
                const proj = projects.find(p => p.id === a.project_id);
                return (
                  <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-50">
                    <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#007934] text-xs font-bold">
                      {res?.name?.split(" ").map(n=>n[0]).join("").slice(0,2) || "?"}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{res?.name || a.resource_id}</p>
                      <p className="text-xs text-gray-400">{proj?.title || a.project_id}</p>
                    </div>
                    <span className="text-sm text-gray-600">{a.hours_allocated}h</span>
                    {a.delivery_impact_days > 0 && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{a.delivery_impact_days} días</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Resource Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-fade-up" data-testid="create-resource-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">{editingResource ? "Editar Recurso" : "Nuevo Recurso"}</h2>
              <button onClick={closeModal}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              {[["name","Nombre *","text",true],["email","Email","email",false],["role","Rol","text",false],["department","Departamento","text",false]].map(([n,label,type,req]) => (
                <div key={n}>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">{label}</label>
                  <input name={n} type={type} required={req} value={form[n]} onChange={e => setForm({...form, [n]: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid={`resource-${n}-input`} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Tipo</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="internal">Interno</option>
                    <option value="external">Externo</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Coste/día (€)</label>
                  <input type="number" min="0" value={form.daily_cost} onChange={e => setForm({...form, daily_cost: parseFloat(e.target.value)||0})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Coste/hora (€) — opcional</label>
                  <input type="number" min="0" step="0.01" value={form.cost_per_hour || ""} placeholder={form.daily_cost ? (form.daily_cost/8).toFixed(2) : "auto"}
                    onChange={e => setForm({...form, cost_per_hour: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Horas disponibles/semana</label>
                <input type="number" min="1" max="40" value={form.available_hours_per_week} onChange={e => setForm({...form, available_hours_per_week: parseInt(e.target.value)||40})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={closeModal} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60" data-testid="save-resource-btn">
                  {saving ? "Guardando..." : editingResource ? "Guardar cambios" : "Crear recurso"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-fade-up">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">Asignar Recurso a Proyecto</h2>
              <button onClick={() => setShowAssignModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleAssign} className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Recurso *</label>
                <select required value={assignForm.resource_id} onChange={e => setAssignForm({...assignForm, resource_id: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="assign-resource-select">
                  <option value="">Seleccionar recurso...</option>
                  {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Proyecto *</label>
                <select required value={assignForm.project_id} onChange={e => setAssignForm({...assignForm, project_id: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="assign-project-select">
                  <option value="">Seleccionar proyecto...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Horas asignadas *</label>
                  <input type="number" required min="1" value={assignForm.hours_allocated}
                    onChange={e => setAssignForm({...assignForm, hours_allocated: parseInt(e.target.value)||0})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="assign-hours-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Rol en proyecto</label>
                  <input value={assignForm.role_in_project} onChange={e => setAssignForm({...assignForm, role_in_project: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowAssignModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60" data-testid="save-assignment-btn">
                  {saving ? "Asignando..." : "Asignar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
